import React, { useContext, useEffect, useRef, useState } from 'react';
import { Box, Paper, Typography, TextField, Stack, CircularProgress } from '@mui/material';
import { AutoComplete, Button, Chip } from 'components/Form';
import { semesters, statuses } from 'keys';
import { semesterAndYearOptions } from 'keys/years';
import { useMutation, useQuery } from '@tanstack/react-query';
import { getStudentSummaryFetch, updateTeacherRecommendationFetch } from 'store/middlewares/assessment.crud';
import { useSelector } from 'react-redux';
import { PaginationArea, ScrollableArea, StudentProgressStyles, SummaryTableStyles } from './Style';
import CreateIcon from '@mui/icons-material/Create';
import CustomPagination from 'components/Table/FooterPagination';
import { initialPage, initialPageSize } from 'keys/pagination';
import { useReactToPrint } from 'react-to-print';
import StudentCard from './StudentCard';
import { LocalPrintshop } from '@mui/icons-material';
import { ratingListLabel, ratingListChipColors, ratingIcons } from 'keys/rating';
import { AssessmentsContext } from './AssessmentsContext';

const StudentProgressCards = ({ data }) => {
    const recommendationsRef = useRef({});

    // הרפרנס להדפסה כללית (עוטף את כל רשימת התלמידים)
    const printAllRef = useRef(null);

    const [page, setPage] = useState(initialPage);
    const [pageSize, setPageSize] = useState(initialPageSize);

    const { headerFilters } = useSelector(state => state.header);
    const { testTaker } = useSelector(state => state.testTaker);

    // שנה משותפת עם שאר תצוגות הדוחות (טבלה, חלוקה לדירוגים, חלוקה לתחומי בדיקה, קבוצות קריאה)
    const { profileFilters, setProfileFilters } = useContext(AssessmentsContext);
    const { semester, school_year } = profileFilters;
    const numOfSemesters = Number(semester?.id) || 3;

    // הגדרת העמודות הדינמיות
    const checkTypes = [
        { id: "wpm", label: "קצב", isPercent: false },
        { id: "accuracy", label: "דיוק", isPercent: true },
        { id: "wcpm", label: "מילים נכונות לדקה", isPercent: false }
    ];
    const dynamicColumnsCount = checkTypes.length * numOfSemesters;

    const studentTable = useQuery({
        queryKey: ["student-summary-table", headerFilters, testTaker, school_year, page, pageSize],
        queryFn: async () => {
            const assessmentFilters = {
                status: statuses.inProgress,
                sub_status: statuses.wordsComplete,
                school_year: school_year
            };
            const { data } = await getStudentSummaryFetch({ assessmentFilters, headerFilters, testTaker, page, pageSize });
            return data;
        }
    });

    const { mutateAsync: updateRecommendation, isPending, variables } = useMutation({
        mutationFn: updateTeacherRecommendationFetch
    });

    useEffect(() => {
        setPage(initialPage)
    }, [headerFilters, testTaker, school_year, pageSize])
    const handlePrintAll = useReactToPrint({
        content: () => printAllRef.current,
        documentTitle: 'דוח הערכת תלמידים',
        pageStyle: `
            @page { size: A4 landscape; margin: 10mm; }
            html, body {
                direction: rtl !important;
                -webkit-print-color-adjust: exact !important;
                print-color-adjust: exact !important;
                background-color: white !important;
            }
            .student-card {
                page-break-inside: avoid;
                margin-bottom: 20px;
                border: 1px solid #E8EEF6;
                border-radius: 8px;
            }
            .grid-container {
                display: grid !important;
                /* חובה: לשכפל לכאן את העמודות כדי שההדפסה תכיר אותן! */
                grid-template-columns: minmax(180px, 1.5fr) repeat(${dynamicColumnsCount}, minmax(60px, 1fr)) minmax(220px, 2fr) !important;
                width: 100% !important;
            }
            .grid-container > div {
                border-bottom: 1px solid #E8EEF6 !important;
                border-left: 1px solid #E8EEF6 !important;
                padding: 12px;
                display: flex;
                align-items: center;
                justify-content: center; /* כברירת מחדל לאמצע */
            }
            /* יישור טקסט לפי מחלקות */
            .grid-container .cell-start { justify-content: flex-start !important; }
            .grid-container .cell-between { justify-content: space-between !important; }
            
            button, .MuiButtonBase-root { display: none !important; }
            .recommendation-field fieldset { border: none !important; }
            .recommendation-field textarea { resize: none; overflow: hidden; }
        `
    });

    if (studentTable.isLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '30vh' }}>
                <CircularProgress color="thirdly" />
            </Box>
        );
    }

    const displayData = studentTable.data || data;

    return (
        <SummaryTableStyles>

            <div className='header'>
                <Stack direction="row" spacing={1} alignItems="center">
                    {ratingListLabel.map((label, i) => (
                        <Chip key={i} color={ratingListChipColors[i]}>
                            <i className={ratingIcons[i]} style={{ marginLeft: 4 }} />
                            {label}
                        </Chip>
                    ))}
                </Stack>
                <Stack direction="row" spacing={1.5} alignItems="center">
                    <AutoComplete
                        placeholder="שנת לימודים"
                        options={semesterAndYearOptions}
                        width={200}
                        hideClear
                        input={{
                            value: school_year,
                            onChange: (value) => setProfileFilters(prev => ({ ...prev, school_year: value }))
                        }}
                    />
                    <Button
                        variant="contained"
                        color="thirdly"
                        action={handlePrintAll}
                        icon={<LocalPrintshop />}
                        label="הדפסת דף נוכחי"
                        disabled={!displayData?.results?.length}
                    />
                </Stack>
            </div>

            <StudentProgressStyles spacing={3} mt={0} columns={dynamicColumnsCount} sx={{ height: 'calc(100vh - 200px)' }}>
                {/* שימי לב: אנו מעבירים את ה-ref ל-ScrollableArea
                    זה מה שיישלח למדפסת כשיש הדפסה כללית.
                */}
                <ScrollableArea ref={printAllRef}>
                    {displayData?.results?.map((student) => (
                        <StudentCard
                            key={student.id}
                            student={student}
                            checkTypes={checkTypes}
                            numOfSemesters={numOfSemesters}
                            semesters={semesters}
                            recommendationsRef={recommendationsRef}
                            updateRecommendation={updateRecommendation}
                            isPending={isPending}
                            variables={variables}
                            dynamicColumnsCount={dynamicColumnsCount}
                        />
                    ))}
                </ScrollableArea>

                <PaginationArea>
                    <CustomPagination
                        count={displayData?.count}
                        page={page}
                        pageSize={pageSize}
                        onPageChange={(newPage) => setPage(newPage)}
                        onPageSizeChange={(newSize) => {
                            setPageSize(newSize);
                            setPage(0);
                        }}
                        loading={studentTable.isFetching}
                    />
                </PaginationArea>
            </StudentProgressStyles>
        </SummaryTableStyles>
    );
};

export default StudentProgressCards;